import type { ComponentType } from "react";
import { useTranslation } from "react-i18next";
import { Image, Target, TrendingUp, Users, type LucideProps } from "lucide-react";
import { cn } from "@nexus/ui";
import { AskNexusMark } from "@/features/ask-nexus/components/AskNexusMark";
import { ASK_NEXUS_ICON_STROKE, askNexusColumnClass } from "@/features/ask-nexus/lib/ui";
import { useAskNexusPanel } from "@/features/ask-nexus/store/ask-nexus-panel.store";

/** Read-only questions the allowed Nexus tools can answer. */
const SUGGESTIONS: Array<{ key: string; fallback: string; icon: ComponentType<LucideProps> }> = [
  { key: "askNexus.empty.suggestions.performance", fallback: "How did my ads do in the last 30 days?", icon: TrendingUp },
  { key: "askNexus.empty.suggestions.competitors", fallback: "What are competitors running this month?", icon: Users },
  { key: "askNexus.empty.suggestions.creatives", fallback: "Which of my creatives performed best recently?", icon: Image },
  { key: "askNexus.empty.suggestions.opportunities", fallback: "Show my top opportunities", icon: Target },
];

/**
 * First screen of a new chat. The prototype sets the heading in Funnel Display;
 * Nexus loads Inter only (DESIGN.md), so the heading keeps the prototype's size,
 * weight, leading and tracking in Inter. It sits near the top, its space above
 * scaling with the screen height, instead of centred in a tall panel.
 */
export function AskNexusEmptyState({
  tenantName,
  canSend,
  onAsk,
}: {
  tenantName: string;
  canSend: boolean;
  onAsk: (question: string) => void;
}) {
  const { t } = useTranslation();
  const isWide = useAskNexusPanel((s) => s.isWide);
  return (
    <div
      className={cn("mx-auto flex flex-col px-6 pt-[clamp(8px,3vh,32px)] pb-2", askNexusColumnClass(isWide, "max-w-[640px]"))}
      data-testid="ask-nexus-empty"
    >
      <AskNexusMark className="block h-8 w-[60px]" />
      <h1 className="mt-4 mb-2 text-[32px] leading-[1.08] font-medium tracking-[-0.03em]">
        {t("askNexus.empty.title", "What do you want to know about {{tenant}}?", { tenant: tenantName })}
      </h1>
      <p className="mb-6 leading-[1.55] text-(--an-ink-2)">
        {t("askNexus.empty.intro", "Ask about your ads, your competitors and your industry. Answers use {{tenant}} data only.", { tenant: tenantName })}
      </p>
      {canSend ? (
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
          {SUGGESTIONS.map((suggestion) => {
            const question = t(suggestion.key, suggestion.fallback);
            const SuggestionIcon = suggestion.icon;
            return (
              <button
                key={suggestion.key}
                type="button"
                className="flex min-h-[92px] flex-col gap-2.5 rounded-2xl border border-(--an-hair) bg-(--an-field) p-3.5 text-left text-[13px] leading-[1.4] transition-colors hover:border-(--an-hair-dashed)"
                onClick={() => onAsk(question)}
              >
                <SuggestionIcon className="size-4 flex-none text-(--an-ink-2)" strokeWidth={ASK_NEXUS_ICON_STROKE} aria-hidden="true" />
                {question}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
